import { Dialog } from '@headlessui/react';
import { CheckIcon, SearchIcon, XIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import React, { FC, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FaCrown } from 'react-icons/fa6';
import { ImageModelType, TrainingModelType } from 'types';

import clsxm from '@/lib/clsxm';

import Modal from '@/components/atoms/Modal';
import Button from '@/components/buttons/Button';
import ButtonLink from '@/components/links/ButtonLink';

import { DEFAULT_GPT_IMAGE_MODEL } from '@/constants/gptImageModels';
import {
  getModelDisplayName,
  isPremiumImageModel,
  modelsWithDescription,
} from '@/constants/imageModels';
import { useUser } from '@/utils/useUser';

interface ModelSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  value?: ImageModelType | ImageModelType[];
  onChange: (value: ImageModelType | ImageModelType[]) => void;
  trainingModels?: TrainingModelType[];
  defaultTrainingModel?: TrainingModelType;
}

interface ModelCard {
  id: string;
  title: string;
  description: string;
  credits?: string;
  isCustom?: boolean;
  icon?: FC<React.SVGProps<SVGSVGElement>>;
  tags?: string[];
  previewImage?: string;
}

type ModelTab = 'models' | 'custom';
const TABS: ModelTab[] = ['models', 'custom'];
const getPreviewThumbUrl = (imageUrl: string) =>
  imageUrl.includes('?')
    ? imageUrl
    : `https://easy-peasy.ai/cdn-cgi/image/quality=70,format=auto,width=160/${imageUrl}`;

export const ModelSelectorModal: FC<ModelSelectorModalProps> = ({
  isOpen,
  onClose,
  value,
  onChange,
  trainingModels = [],
  defaultTrainingModel,
}) => {
  const { t, lang } = useLocale('ai-images');
  const { isFreePlan } = useUser();
  const [activeTab, setActiveTab] = useState<ModelTab>('models');
  const [search, setSearch] = useState('');
  const [selectionMode, setSelectionMode] = useState<'single' | 'multi'>(
    'single',
  );
  const [draftSelection, setDraftSelection] = useState<string[]>([]);
  const wasOpen = useRef(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const id = useId();

  const { customModels, standardModels, selectedModels } = useMemo(() => {
    const training = defaultTrainingModel
      ? [defaultTrainingModel, ...trainingModels]
      : trainingModels;
    const custom: ModelCard[] = Array.from(
      new Map(training.map((model) => [model.id, model])).values(),
    ).map((model) => ({
      id: String(model.id),
      title: model.name || '',
      description: '',
      isCustom: true,
      previewImage: Array.isArray(model.images)
        ? (model.images as string[]).find(
            (url) => typeof url === 'string' && url.trim() !== '',
          )
        : undefined,
    }));
    const standard: ModelCard[] = modelsWithDescription.map((model) => ({
      id: model.id,
      title: model.title,
      description: model.description,
      credits: model.users,
      icon: model.icon,
      tags: model.tags,
    }));
    const incoming = Array.isArray(value)
      ? value
      : [value || defaultTrainingModel?.name || DEFAULT_GPT_IMAGE_MODEL];
    const allModels = [...custom, ...standard];
    const selected = [
      ...new Set(
        incoming.map(
          (name) =>
            allModels.find((model) => model.id === name || model.title === name)
              ?.title || name,
        ),
      ),
    ];
    return {
      customModels: custom,
      standardModels: standard,
      selectedModels: selected,
    };
  }, [trainingModels, defaultTrainingModel, value]);

  useEffect(() => {
    // Initialize once per opening, so changing tabs or searching cannot reset a draft.
    if (isOpen && !wasOpen.current) {
      const hasCustomModel = selectedModels.some((name) =>
        customModels.some((model) => model.title === name),
      );
      setActiveTab(hasCustomModel ? 'custom' : 'models');
      setSelectionMode(
        !hasCustomModel && selectedModels.length > 1 && !isFreePlan
          ? 'multi'
          : 'single',
      );
      setDraftSelection(selectedModels);
      setSearch('');
    }
    wasOpen.current = isOpen;
  }, [isOpen, selectedModels, customModels, isFreePlan]);

  const isMultiSelect = activeTab === 'models' && selectionMode === 'multi';
  const tabModels = activeTab === 'custom' ? customModels : standardModels;
  const query = search.trim().toLocaleLowerCase(lang);
  const displayedModels = tabModels.filter(
    (model) =>
      !query ||
      `${model.title} ${getModelDisplayName(model.title)} ${model.description} ${model.tags?.join(' ') || ''}`
        .toLocaleLowerCase(lang)
        .includes(query),
  );
  // Custom models cannot be combined with image engines in a multi-model request.
  const standardDraft = draftSelection.filter(
    (name) =>
      standardModels.some((model) => model.title === name) &&
      (!isFreePlan || !isPremiumImageModel(name)),
  );
  const baseCredits = standardDraft.reduce((total, name) => {
    const credits = standardModels.find(
      (model) => model.title === name,
    )?.credits;
    const match = credits?.match(/(\d+(?:\.\d+)?)\s*credit/i);
    return total + (match ? Number(match[1]) : 0);
  }, 0);

  const selectTab = (tab: ModelTab) => {
    setActiveTab(tab);
    setSearch('');
  };
  const onTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number;
    if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TABS.length - 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
      nextIndex = 1 - index;
    else return;
    event.preventDefault();
    selectTab(TABS[nextIndex]);
    tabRefs.current[nextIndex]?.focus();
  };
  const toggleMultiSelect = () => {
    if (isFreePlan) return;
    if (selectionMode === 'single') {
      setDraftSelection(
        standardDraft.length ? standardDraft : [DEFAULT_GPT_IMAGE_MODEL],
      );
      setSelectionMode('multi');
    } else {
      setSelectionMode('single');
    }
  };
  const selectModel = (model: ModelCard) => {
    if (!model.isCustom && isFreePlan && isPremiumImageModel(model.title))
      return;
    if (model.isCustom || selectionMode === 'single') {
      onChange(model.title as ImageModelType);
      onClose();
      return;
    }
    setDraftSelection((previous) =>
      previous.includes(model.title)
        ? previous.length > 1
          ? previous.filter((name) => name !== model.title)
          : previous
        : [...previous, model.title],
    );
  };

  const renderModelCard = (model: ModelCard) => {
    const selected = (isMultiSelect ? standardDraft : selectedModels).includes(
      model.title,
    );
    const locked =
      !model.isCustom && isFreePlan && isPremiumImageModel(model.title);
    const name = model.isCustom
      ? model.title || t('model_picker.unnamed', { id: model.id })
      : getModelDisplayName(model.title);
    const isNew = model.tags?.includes('New');
    return (
      <button
        key={model.id}
        type='button'
        onClick={() => selectModel(model)}
        disabled={locked}
        aria-label={name}
        aria-pressed={selected}
        title={model.isCustom ? name : t(model.description)}
        className={clsxm(
          'group relative flex min-w-0 rounded-xl border p-4 text-start transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
          model.isCustom ? 'items-center gap-3' : 'h-full flex-col gap-3',
          selected
            ? 'border-primary-500 bg-primary-50/60 ring-1 ring-primary-500 dark:bg-primary-900/20'
            : 'border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900',
          locked
            ? 'cursor-not-allowed opacity-60'
            : 'hover:border-primary-300 hover:bg-gray-50 dark:hover:border-primary-600 dark:hover:bg-slate-800',
        )}
      >
        {model.isCustom ? (
          <>
            <div className='relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100 text-base font-medium text-gray-500 dark:bg-slate-800 dark:text-gray-400'>
              <span aria-hidden='true'>
                {(model.title.trim().charAt(0) || '?').toUpperCase()}
              </span>
              {model.previewImage && (
                // User image URLs use the CDN thumbnail above, with a direct-source fallback.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={getPreviewThumbUrl(model.previewImage)}
                  alt=''
                  width={48}
                  height={48}
                  loading='lazy'
                  decoding='async'
                  className='absolute inset-0 h-full w-full object-cover'
                  onError={(event) => {
                    const image = event.currentTarget;
                    if (!image.dataset.fallback && model.previewImage) {
                      image.dataset.fallback = 'true';
                      image.src = model.previewImage;
                    } else image.style.visibility = 'hidden';
                  }}
                />
              )}
            </div>
            <span className='min-w-0 flex-1 break-words text-sm font-medium leading-snug text-gray-900 dark:text-white'>
              {name}
            </span>
            <span
              className={clsxm(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                selected
                  ? 'bg-primary-600 text-white'
                  : 'border border-gray-200 dark:border-slate-600',
              )}
              aria-hidden='true'
            >
              {selected && <CheckIcon className='h-3.5 w-3.5' />}
            </span>
          </>
        ) : (
          <>
            <div className='flex min-h-5 w-full items-center gap-2.5'>
              {model.icon &&
                React.createElement(model.icon, {
                  className: 'h-5 w-5 shrink-0',
                  'aria-hidden': true,
                })}
              <span className='min-w-0 flex-1 break-words text-sm font-semibold leading-snug text-gray-900 dark:text-white'>
                {name}
              </span>
              {selected ? (
                <CheckIcon
                  className='h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400'
                  aria-hidden='true'
                />
              ) : isNew ? (
                <span className='shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'>
                  {t('model_picker.new')}
                </span>
              ) : null}
            </div>
            <p className='line-clamp-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400'>
              {t(model.description)}
            </p>
            <div className='mt-auto w-full space-y-2 pt-1'>
              <p className='text-xs font-medium text-gray-700 dark:text-gray-300'>
                {model.credits}
              </p>
              {locked ? (
                <span className='inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300'>
                  <FaCrown className='h-3 w-3' aria-hidden='true' />
                  {t('model_picker.paid')}
                </span>
              ) : model.tags?.some((tag) => tag !== 'New') ? (
                <div className='flex flex-wrap gap-1.5'>
                  {model.tags
                    .filter((tag) => tag !== 'New')
                    .map((tag) => (
                      <span
                        key={tag}
                        className='rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-slate-800 dark:text-gray-400'
                      >
                        {tag}
                      </span>
                    ))}
                </div>
              ) : null}
            </div>
          </>
        )}
      </button>
    );
  };

  return (
    <Modal
      showModal={isOpen}
      onClose={onClose}
      hideCloseButton
      className='w-full rounded-none sm:w-[calc(100%-3rem)] sm:max-w-6xl sm:rounded-2xl'
    >
      <div
        dir={lang === 'ar' ? 'rtl' : 'ltr'}
        className='flex h-[100dvh] flex-col text-start sm:h-[88dvh] sm:max-h-[900px]'
      >
        <header className='shrink-0 border-b border-gray-200 px-4 pt-5 dark:border-slate-700 sm:px-6'>
          <div className='mb-5 flex items-start justify-between gap-4'>
            <div>
              <Dialog.Title className='text-xl font-semibold tracking-tight text-gray-900 dark:text-white'>
                {t('model_picker.title')}
              </Dialog.Title>
              <Dialog.Description className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                {activeTab === 'custom'
                  ? t('model_picker.custom_hint')
                  : t('model_picker.models_hint')}
              </Dialog.Description>
            </div>
            <button
              type='button'
              onClick={onClose}
              aria-label={t('Close')}
              className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 dark:hover:bg-slate-800'
            >
              <XIcon className='h-5 w-5' aria-hidden='true' />
            </button>
          </div>
          <div
            role='tablist'
            aria-label={t('model_picker.title')}
            className='-mb-px flex gap-5 sm:gap-7'
          >
            {TABS.map((tab, index) => (
              <button
                key={tab}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                type='button'
                role='tab'
                id={`${id}-${tab}-tab`}
                aria-controls={`${id}-${tab}-panel`}
                aria-selected={activeTab === tab}
                aria-label={t(
                  tab === 'models'
                    ? 'model_picker.models'
                    : 'model_picker.custom_models',
                )}
                tabIndex={activeTab === tab ? 0 : -1}
                onClick={() => selectTab(tab)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                className={clsxm(
                  'flex min-h-12 min-w-0 items-center gap-2 border-b-2 pb-3 pt-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                  activeTab === tab
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white',
                )}
              >
                {t(
                  tab === 'models'
                    ? 'model_picker.models'
                    : 'model_picker.custom_models',
                )}
                <span
                  aria-hidden='true'
                  className='rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-normal text-gray-500 dark:bg-slate-800 dark:text-gray-400'
                >
                  {tab === 'models'
                    ? standardModels.length
                    : customModels.length}
                </span>
              </button>
            ))}
          </div>
        </header>

        <div className='flex shrink-0 flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6'>
          <div className='relative w-full sm:max-w-sm'>
            <SearchIcon
              className='pointer-events-none absolute start-3 top-3 h-5 w-5 text-gray-400'
              aria-hidden='true'
            />
            <input
              type='text'
              aria-label={t('model_picker.search')}
              placeholder={t('model_picker.search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className='h-11 w-full rounded-lg border-gray-200 bg-gray-50 pe-11 ps-10 text-sm focus:border-primary-500 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white'
            />
            {search && (
              <button
                type='button'
                aria-label={t('model_picker.clear_search')}
                onClick={() => setSearch('')}
                className='absolute end-0 top-0 flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 focus-visible:outline focus-visible:outline-primary-500'
              >
                <XIcon className='h-4 w-4' aria-hidden='true' />
              </button>
            )}
          </div>
          {activeTab === 'models' ? (
            <div className='flex shrink-0 items-center gap-3'>
              <span
                id={`${id}-multi-label`}
                className='text-sm font-medium text-gray-700 dark:text-gray-300'
              >
                {t('model_picker.multi')}
              </span>
              <button
                type='button'
                role='switch'
                aria-checked={selectionMode === 'multi'}
                aria-labelledby={`${id}-multi-label`}
                aria-describedby={`${id}-multi-hint`}
                disabled={isFreePlan}
                onClick={toggleMultiSelect}
                className='relative inline-flex h-11 w-11 shrink-0 items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-50'
              >
                <span
                  aria-hidden='true'
                  className={clsxm(
                    'inline-flex h-6 w-11 items-center rounded-full p-0.5 transition-colors',
                    selectionMode === 'multi'
                      ? 'bg-primary-600'
                      : 'bg-gray-200 dark:bg-slate-600',
                  )}
                >
                  <span
                    className={clsxm(
                      'h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                      selectionMode === 'multi' &&
                        (lang === 'ar' ? '-translate-x-5' : 'translate-x-5'),
                    )}
                  />
                </span>
              </button>
              <span
                id={`${id}-multi-hint`}
                className={
                  isFreePlan
                    ? 'text-xs text-gray-500 dark:text-gray-400'
                    : 'sr-only'
                }
              >
                {t(
                  isFreePlan ? 'model_picker.paid' : 'model_picker.multi_hint',
                )}
              </span>
            </div>
          ) : customModels.length > 0 ? (
            <ButtonLink
              href='/ai-images/models'
              variant='light'
              className='min-h-11 shrink-0 justify-center text-sm'
            >
              {t('model_picker.manage')}
            </ButtonLink>
          ) : null}
        </div>

        <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-1 sm:px-6'>
          {TABS.map((tab) => (
            <div
              key={tab}
              id={`${id}-${tab}-panel`}
              role='tabpanel'
              aria-labelledby={`${id}-${tab}-tab`}
              hidden={activeTab !== tab}
              tabIndex={0}
              className='outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500'
            >
              {activeTab === tab &&
                (displayedModels.length ? (
                  <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                    {displayedModels.map(renderModelCard)}
                  </div>
                ) : (
                  <div className='mx-auto max-w-sm py-14 text-center'>
                    <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
                      {t(
                        search
                          ? 'model_picker.no_results'
                          : 'model_picker.empty_custom',
                      )}
                    </h3>
                    {search ? (
                      <Button
                        onClick={() => setSearch('')}
                        variant='light'
                        className='mt-4'
                      >
                        {t('model_picker.clear_search')}
                      </Button>
                    ) : (
                      <>
                        <p className='mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400'>
                          {t('model_picker.empty_hint')}
                        </p>
                        <ButtonLink
                          href='/ai-images/models/train'
                          variant='primary'
                          className='mt-5 min-h-11 text-sm'
                        >
                          {t('packs.create_model')}
                        </ButtonLink>
                      </>
                    )}
                  </div>
                ))}
            </div>
          ))}
        </div>

        <footer className='flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-4 dark:border-slate-700 sm:px-6'>
          <div
            className='min-w-0 flex-1 text-xs text-gray-500 dark:text-gray-400'
            role='status'
            aria-live='polite'
          >
            {isMultiSelect ? (
              <>
                <p className='font-medium text-gray-900 dark:text-white'>
                  {t('model_picker.selected', { count: standardDraft.length })}
                  {' · '}
                  {t('model_picker.credits_from', {
                    count: Number(baseCredits.toFixed(2)),
                  })}
                </p>
                <p className='mt-1'>{t('model_picker.credit_hint')}</p>
              </>
            ) : (
              t(
                activeTab === 'custom' && selectionMode === 'multi'
                  ? 'model_picker.custom_single'
                  : 'model_picker.single_hint',
              )
            )}
          </div>
          <div className='flex items-center gap-2'>
            <Button onClick={onClose} variant='light' className='min-h-11'>
              {t('Cancel')}
            </Button>
            {isMultiSelect && (
              <Button
                variant='primary'
                disabled={standardDraft.length === 0 || isFreePlan}
                onClick={() => {
                  onChange(standardDraft as ImageModelType[]);
                  onClose();
                }}
                className='min-h-11'
              >
                {t('model_picker.apply')}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </Modal>
  );
};
